import { calculateMatchAdvantage } from "../../league-duel/engine/combat";
import type { EquipmentStats } from "../../league-duel/engine/combat";
import { getEncounter, DEFAULT_TIER } from "../content/pveTiers";
import type { PveEncounter } from "../content/pveTiers";
import { getEnemy } from "../content/pveEnemies";
import { getBoost } from "../content/pveBoosts";
import type { PveBoostId } from "../content/pveBoosts";
import { getBossEffect } from "../content/pveBossEffects";

// Kept name for compatibility across the feature; a "bonus" is now a boost id.
export type PveBonus = PveBoostId;

export const BONUS_STAGES = [1, 3, 5, 7, 9];
export const MAX_ACTIVE_BONUSES = 3;
export const MAX_CONDITION = 100;
// Regen is ALWAYS 0%→100% in exactly 10 min, whatever the player's maxHp.
export const FULL_REGEN_SECONDS = 600;
export const CONDITION_REGEN_MS = FULL_REGEN_SECONDS * 1000;
// Vie (HP) is derived from the vitesse stat: 1 vitesse = 10 HP in combat.
export const PVE_HP_PER_VITESSE = 10;

/**
 * Player max HP derived from the vitesse stat (1 vitesse = 10 HP). V1: flat, no
 * boost modifies max HP (a future maxHpMultiplier boost would multiply here).
 */
export function maxHpFromStats(vitesse: number): number {
  return Math.round(vitesse * PVE_HP_PER_VITESSE);
}

/** Real HP for a given maxHp, from the 0..100 condition percentage (decimal). */
export function hpFromCondition(conditionPct: number, maxHp: number): number {
  return (conditionPct / MAX_CONDITION) * maxHp;
}

/**
 * Percentage-based regen in HP units. Rate = maxHp / 600 per second, so a full
 * 0→100% recovery always takes 10 min regardless of maxHp. Never rounds — sub-HP
 * regen accumulates. Round only at display time.
 */
export function calculateCurrentHp(
  currentHp: number,
  maxHp: number,
  updatedAt: string | Date,
  now = Date.now()
): number {
  const elapsedSeconds = Math.max(0, (now - new Date(updatedAt).getTime()) / 1000);
  const regenPerSecond = maxHp / FULL_REGEN_SECONDS;
  return Math.min(maxHp, currentHp + elapsedSeconds * regenPerSecond);
}

// Temporary placeholder opponent artwork for the upcoming combat animation.
export const STADIUM_TRIAL_TEST_OPPONENT_IMG =
  "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/player%20test.png";

// Deterministic rewards per stage — intentionally small to not break economy.
// NOTE: xp maps to bonus_points which also feeds wallet (+gold side effect — see playerEngine.ts:130).
const STAGE_REWARDS: Record<number, { xp: number; gold: number; chest: boolean }> = {
  1:  { xp: 4,  gold: 2,  chest: false },
  2:  { xp: 5,  gold: 2,  chest: false },
  3:  { xp: 6,  gold: 3,  chest: false },
  4:  { xp: 7,  gold: 3,  chest: false },
  5:  { xp: 8,  gold: 4,  chest: false },
  6:  { xp: 9,  gold: 4,  chest: false },
  7:  { xp: 10, gold: 5,  chest: false },
  8:  { xp: 11, gold: 5,  chest: false },
  9:  { xp: 13, gold: 6,  chest: false },
  10: { xp: 25, gold: 15, chest: true  },
};

export interface ConditionState {
  condition: number;
  msToFull: number;
  isFull: boolean;
}

/**
 * Time-based regen — pure, no network. Fills from `base` toward `ceiling`, always
 * taking 10 min (CONDITION_REGEN_MS) for a full 0→ceiling recovery, whatever the
 * ceiling. `ceiling` defaults to MAX_CONDITION (100); pass maxConditionWithBonuses
 * to regen correctly when the player carries condition bonuses (mid-run camp).
 */
export function calculatePveCondition(
  base: number,
  updatedAt: string,
  now = Date.now(),
  ceiling = MAX_CONDITION
): ConditionState {
  const elapsed = Math.max(0, now - new Date(updatedAt).getTime());
  const regenPoints = (elapsed / CONDITION_REGEN_MS) * ceiling;
  const condition = Math.min(ceiling, base + regenPoints);
  if (condition >= ceiling) return { condition: ceiling, msToFull: 0, isFull: true };
  const msToFull = ((ceiling - condition) / ceiling) * CONDITION_REGEN_MS;
  // Keep the decimal internally — sub-point regen must not be lost. Round only in the UI.
  return { condition, msToFull, isFull: false };
}

export function formatConditionTime(ms: number): string {
  const m = Math.ceil(ms / 60000);
  if (m >= 60) return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
  return `${m}min`;
}

/**
 * Condition ceiling. V1: flat MAX_CONDITION — no boost raises it anymore. Kept as
 * a function (ignoring its argument) so the many callers stay untouched, and so a
 * future max-condition boost has a single place to hook.
 */
export function maxConditionWithBonuses(_bonuses?: PveBonus[]): number {
  return MAX_CONDITION;
}

// ── Native per-stat combat model ────────────────────────────────────────────
// Base stats (from equipped items) drive combat directly:
//   Physique → Puissance  : damage per hit = physique × (0.90 + rand×0.20)  (±10%)
//   Technique → Technique  : crit ×2, chance = clamp(0.60·own/(own+opp) − 0.20, .05, .30)
//   Vitesse  → Vie         : maxHp = vitesse × 10 (PVE_HP_PER_VITESSE)
//   Défense  → Défense     : reduction = min(0.50, defense / (defense + 300))
export const TECHNIQUE_MIN_CHANCE = 0.05;
export const TECHNIQUE_MAX_CHANCE = 0.30;
export const TECHNIQUE_BASE_CHANCE = 0.10; // at equal technique
// Diminishing-returns denominator. 50% reduction ⇔ armor == DEFENSE_CONSTANT, so
// this is tuned to ≈ an END-GAME defensive set (a full defensive ultra kit peaks
// near this). Mid-leveling armor sits well below 50% on purpose (Dofus-like: 50%
// res is a tank/endgame achievement, not a mid-progression baseline). e.g. 138
// armor (a defensive L33 set) → 31.5%, not 41%.
export const DEFENSE_CONSTANT = 300;
// Hard cap on damage reduction — armor can never exceed 50% (past ~endgame the
// curve is clamped so stacking further armor is dead weight).
export const MAX_DEFENSE_REDUCTION = 0.50;
export const CRIT_MULTIPLIER = 2;
const DAMAGE_ROLL = 0.10;        // ±10% per hit
const COMBAT_MAX_TURNS = 60;     // safety cap (min 1 dmg guarantees termination well before)

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Crit chance of `own` technique attacking a defender with `opp` technique. */
export function critChance(own: number, opp: number): number {
  const total = own + opp;
  if (total <= 0) return TECHNIQUE_BASE_CHANCE;
  return clamp(0.60 * (own / total) - 0.20, TECHNIQUE_MIN_CHANCE, TECHNIQUE_MAX_CHANCE);
}

/** Damage reduction (0..1) granted by a `defense` stat — capped at
 *  MAX_DEFENSE_REDUCTION so armor can never mitigate more than 50%. */
export function defenseReductionFor(defense: number): number {
  return Math.min(MAX_DEFENSE_REDUCTION, defense / (defense + DEFENSE_CONSTANT));
}

/** One hit: attacker vs defender. Pure except for the injected rng.
 * Exported for the interactive variant engine (pveCombatInteractive) so both
 * variants share the exact same base-hit math. */
export function computeHit(atk: EquipmentStats, def: EquipmentStats, dmgMult: number, rng: () => number) {
  const base = atk.physique * (1 - DAMAGE_ROLL + rng() * 2 * DAMAGE_ROLL); // ±10%
  const crit = rng() < critChance(atk.technique, def.technique);
  const raw = (crit ? base * CRIT_MULTIPLIER : base) * dmgMult;
  const damage = Math.max(1, raw * (1 - defenseReductionFor(def.defense)));
  return { damage, crit };
}

// ── Boost / boss effect resolution ──────────────────────────────────────────
// Active boosts are collapsed ONCE per fight into a flat CombatModifiers object
// that the combat loop consumes at its hook points. Multiplicative effects stack
// as products; per-proc effects (heal) are kept as a list so multiple sources
// stack naturally. Unknown/legacy ids (e.g. old "physique") are skipped.

export interface CombatModifiers {
  playerDamageMult: number;   // × on player outgoing damage (product)
  playerDefenseMult: number;  // × on player defense stat (glass cannon → 0)
  maxHpMult: number;          // × on max HP (Greed → 0.85)
  rewardMult: number;         // × on this fight's XP & gold rewards (Greed → 1.15)
  startShieldPercent: number; // start-of-fight shield (% max HP, summed)
  startShieldFlat: number;    // start-of-fight shield (flat points, summed)
  missingHpDamageFactor: number; // Bloodlust: +factor × missing-HP fraction at fight start
  counterOnEnemyCrit: boolean;   // Riposte: extra player attack when the enemy crits
  execute: { threshold: number; mult: number } | null; // vs low-HP enemies
  heals: { chance: number; missingPct: number }[];      // proc after player attacks
  lastStand: { threshold: number; shieldPercent: number } | null; // once/fight
}

export function resolveCombatModifiers(boostIds: PveBonus[]): CombatModifiers {
  const m: CombatModifiers = {
    playerDamageMult: 1,
    playerDefenseMult: 1,
    maxHpMult: 1,
    rewardMult: 1,
    startShieldPercent: 0,
    startShieldFlat: 0,
    missingHpDamageFactor: 0,
    counterOnEnemyCrit: false,
    execute: null,
    heals: [],
    lastStand: null,
  };
  for (const id of boostIds) {
    const e = getBoost(id)?.effects;
    if (!e) continue; // unknown/legacy id → no effect
    if (e.damageMultiplier != null) m.playerDamageMult *= e.damageMultiplier;
    if (e.defenseMultiplier != null) m.playerDefenseMult *= e.defenseMultiplier;
    if (e.maxHpMultiplier != null) m.maxHpMult *= e.maxHpMultiplier;
    if (e.rewardMultiplier != null) m.rewardMult *= e.rewardMultiplier;
    if (e.startFightShieldPercent != null) m.startShieldPercent += e.startFightShieldPercent;
    if (e.startFightShieldFlat != null) m.startShieldFlat += e.startFightShieldFlat;
    if (e.damagePerMissingHpStart != null) m.missingHpDamageFactor += e.damagePerMissingHpStart;
    if (e.counterOnEnemyCrit) m.counterOnEnemyCrit = true;
    if (e.executeDamageMultiplier != null && e.executeThresholdPercent != null) {
      m.execute = { threshold: e.executeThresholdPercent / 100, mult: e.executeDamageMultiplier };
    }
    if (e.healProcChance != null && e.healMissingHpPercent != null) {
      m.heals.push({ chance: e.healProcChance, missingPct: e.healMissingHpPercent / 100 });
    }
    if (e.triggerHpThresholdPercent != null && e.shieldPercent != null) {
      m.lastStand = { threshold: e.triggerHpThresholdPercent / 100, shieldPercent: e.shieldPercent / 100 };
    }
  }
  return m;
}

export interface BossModifiers {
  berserker: { gainPerStep: number; stepPercent: number } | null;
}

export function resolveBossModifiers(effectIds: string[] | undefined): BossModifiers {
  const mods: BossModifiers = { berserker: null };
  for (const id of effectIds ?? []) {
    const e = getBossEffect(id)?.effects;
    if (!e) continue;
    if (e.damageGainPerMissingHpStep != null && e.missingHpStepPercent != null) {
      mods.berserker = { gainPerStep: e.damageGainPerMissingHpStep, stepPercent: e.missingHpStepPercent / 100 };
    }
  }
  return mods;
}

/**
 * Enemy 4-stat block:
 *   - boss → the encounter's MANUAL `statBlock`, used verbatim.
 *   - mob  → the encounter's fixed `mobBudget` spread over the enemy's statWeights
 *            (shape: striker/tank/…). Budget already includes the room multiplier.
 * Falls back to a small balanced block if data is missing (never zero).
 * Exported for the interactive variant engine (pveCombatInteractive) — both
 * variants must fight the exact same enemy stat block.
 */
export function enemyStatsFor(encounter: PveEncounter | undefined): EquipmentStats {
  if (encounter?.statBlock) {
    const s = encounter.statBlock;
    return {
      physique: Math.max(1, s.physique),
      technique: Math.max(1, s.technique),
      vitesse: Math.max(1, s.vitesse),
      defense: Math.max(1, s.defense),
    };
  }
  const budget = encounter?.mobBudget ?? 40;
  const w = (encounter ? getEnemy(encounter.enemyId) : undefined)?.statWeights
    ?? { physique: 0.25, technique: 0.25, vitesse: 0.25, defense: 0.25 };
  const sum = w.physique + w.technique + w.vitesse + w.defense || 1;
  return {
    physique: Math.max(1, budget * w.physique / sum),
    technique: Math.max(1, budget * w.technique / sum),
    vitesse: Math.max(1, budget * w.vitesse / sum),
    defense: Math.max(1, budget * w.defense / sum),
  };
}

// Enemy stat block + derived HP for the pre-fight "Check Stats" popup (Camp).
// Mirrors exactly what simulateCombat uses, so the preview matches the real fight.
export interface EnemyStatPreview {
  physique: number;
  technique: number;
  vitesse: number;
  defense: number;
  maxHp: number;            // vitesse × PVE_HP_PER_VITESSE (as in simulateCombat)
  damageMultiplier: number;
}

export function getEnemyStatPreview(tier: number, stage: number): EnemyStatPreview | undefined {
  const encounter = getEncounter(tier, stage);
  if (!encounter) return undefined;
  const s = enemyStatsFor(encounter);
  return {
    physique: Math.round(s.physique),
    technique: Math.round(s.technique),
    vitesse: Math.round(s.vitesse),
    defense: Math.round(s.defense),
    maxHp: Math.max(1, Math.round(s.vitesse * PVE_HP_PER_VITESSE)),
    damageMultiplier: encounter.damageMultiplier ?? 1,
  };
}

export interface BossResult {
  win: boolean;
  damageReceived: number; // real HP lost this fight
  conditionAfter: number; // % on the maxConditionWithBonuses scale (persisted)
  xpEarned: number;
  goldEarned: number;
  chestEarned: boolean;
  winChance: number; // rough pre-fight estimate (display only)
}

// ── Turn-by-turn combat (the simulation IS the source of truth) ─────────────
export type CombatActor = "player" | "boss";

export interface CombatFrame {
  turn: number;
  attacker: CombatActor;
  damage: number;
  crit: boolean;
  playerHp: number;
  bossHp: number;
  playerShield: number;   // remaining player shield AFTER this frame (0 if none)
  heal?: number;          // HP restored this frame (Heal proc, player turns only)
  lastStand?: boolean;    // Last Stand shield triggered on this frame
  counter?: boolean;      // this player attack is a Riposte (extra attack after an enemy crit)
  spell?: string;         // spells variant only: the spell cast on this frame (PveSpellId)
  selfDamage?: number;    // spells variant only: HP paid by the caster (Reckless Strike)
  // 2v2 pets test (spells variant only) — attacker stays the SIDE ("player" /
  // "boss") so hit directions & popups keep working; actor refines WHO acted.
  actor?: "player" | "boss" | "allyPet" | "enemyPet";
  petHp?: number;         // ally pet HP AFTER this frame
  enemyPetHp?: number;    // enemy pet HP AFTER this frame
  targetPet?: boolean;    // this frame's damage landed on a PET (not the master)
}

export interface CombatSequence {
  frames: CombatFrame[];
  result: BossResult;
  playerMaxHp: number;
  bossMaxHp: number;
}

/**
 * Real-HP, per-stat turn-by-turn fight. The simulation itself decides the outcome
 * (no separate pre-roll): player and enemy trade blows until one drops. Player
 * goes first, so exact ties favour the player. Pure except for `Math.random`.
 *
 * Active boosts + boss effects are resolved to modifiers applied at the loop's
 * hook points (shield, execute, heal, glass cannon, last stand, berserker). Both
 * the frames AND the result come out of THIS single run, so what the animation
 * shows always matches the persisted outcome.
 *
 * Condition (%) is converted to real HP on entry and back to % on exit, so the
 * persisted `pve_condition` stays the maxHp-independent percentage.
 */
export function simulateCombat(
  stage: number,
  playerStats: EquipmentStats,
  activeBonuses: PveBonus[],
  currentCondition: number,
  tier: number = DEFAULT_TIER
): CombatSequence {
  const encounter = getEncounter(tier, stage);
  const baseTeamPower = playerStats.physique + playerStats.technique + playerStats.vitesse + playerStats.defense;

  const mods = resolveCombatModifiers(activeBonuses);
  const bossMods = resolveBossModifiers(encounter?.effects);

  const eStats = enemyStatsFor(encounter);
  const dmgMult = encounter?.damageMultiplier ?? 1;
  // Player as DEFENDER: defense stat scaled by boosts (glass cannon → 0).
  const playerDef: EquipmentStats = { ...playerStats, defense: playerStats.defense * mods.playerDefenseMult };

  const condMax = Math.max(1, maxConditionWithBonuses(activeBonuses));
  const playerMaxHp = Math.max(1, Math.round(maxHpFromStats(playerStats.vitesse) * mods.maxHpMult));
  const bossMaxHp = Math.max(1, Math.round(eStats.vitesse * PVE_HP_PER_VITESSE));

  const startCond = clamp(currentCondition, 0, condMax);
  const startHp = (startCond / condMax) * playerMaxHp;

  const rng = Math.random;
  const frames: CombatFrame[] = [];
  let playerHp = startHp;
  let bossHp = bossMaxHp;
  // Shield absorbs damage before HP; resets each fight (never persisted). Flat + %.
  let playerShield = (mods.startShieldPercent / 100) * playerMaxHp + mods.startShieldFlat;
  let lastStandUsed = false;
  let turn = 0;

  // Bloodlust: damage bonus from missing HP, LOCKED at fight start (shield doesn't
  // count as HP, so a shielded low-HP entry keeps the full bonus).
  const startMissingFrac = Math.max(0, Math.min(1, 1 - startHp / playerMaxHp));
  const playerAtkMult = mods.playerDamageMult * (1 + startMissingFrac * mods.missingHpDamageFactor);

  // Once-per-fight reactive shield. Returns true the turn it fires.
  const tryLastStand = (): boolean => {
    if (!mods.lastStand || lastStandUsed) return false;
    if (playerHp > 0 && (playerHp / playerMaxHp) < mods.lastStand.threshold) {
      playerShield += mods.lastStand.shieldPercent * playerMaxHp;
      lastStandUsed = true;
      return true;
    }
    return false;
  };
  // Fire immediately if the fight starts already below the threshold.
  tryLastStand();

  // One player attack (used by normal turns AND Riposte counters). Applies damage
  // mult + bloodlust + execute, then heal procs, then pushes the frame.
  const doPlayerAttack = (isCounter: boolean) => {
    const hit = computeHit(playerStats, eStats, 1, rng);
    let damage = hit.damage * playerAtkMult;
    if (mods.execute && bossHp / bossMaxHp < mods.execute.threshold) damage *= mods.execute.mult;
    bossHp = Math.max(0, bossHp - damage);
    let healed = 0;
    for (const h of mods.heals) {
      if (rng() < h.chance) {
        const amt = h.missingPct * (playerMaxHp - playerHp);
        if (amt > 0) { playerHp = Math.min(playerMaxHp, playerHp + amt); healed += amt; }
      }
    }
    frames.push({
      turn, attacker: "player", damage: Math.round(damage), crit: hit.crit,
      playerHp: Math.round(playerHp), bossHp: Math.round(bossHp),
      playerShield: Math.round(playerShield),
      heal: healed > 0 ? Math.round(healed) : undefined,
      counter: isCounter || undefined,
    });
  };

  while (playerHp > 0 && bossHp > 0 && turn < COMBAT_MAX_TURNS) {
    turn += 1;
    if (turn % 2 === 1) {
      doPlayerAttack(false);
    } else {
      // ── Enemy attacks ── (flat damageMultiplier × dynamic berserker scaling)
      const berserk = bossMods.berserker
        ? 1 + Math.floor((1 - bossHp / bossMaxHp) / bossMods.berserker.stepPercent) * bossMods.berserker.gainPerStep
        : 1;
      const hit = computeHit(eStats, playerDef, dmgMult * berserk, rng);
      const absorbed = Math.min(playerShield, hit.damage);
      playerShield -= absorbed;
      playerHp = Math.max(0, playerHp - (hit.damage - absorbed));
      const ls = tryLastStand();
      frames.push({
        turn, attacker: "boss", damage: Math.round(hit.damage), crit: hit.crit,
        playerHp: Math.round(playerHp), bossHp: Math.round(bossHp),
        playerShield: Math.round(playerShield),
        lastStand: ls || undefined,
      });
      // Riposte: an enemy crit grants an immediate counter-attack (if both alive).
      if (hit.crit && mods.counterOnEnemyCrit && playerHp > 0 && bossHp > 0) {
        doPlayerAttack(true);
      }
    }
  }

  // Outcome: enemy down (and player alive) = win. On the safety-cap timeout both
  // are alive → decide by remaining HP fraction. Player-favoured on exact ties.
  let win: boolean;
  if (bossHp <= 0 && playerHp > 0) win = true;
  else if (playerHp <= 0) win = false;
  else win = (playerHp / playerMaxHp) >= (bossHp / bossMaxHp);

  // Condition drained = HP lost (shields absorb without touching condition).
  const conditionAfter = clamp((playerHp / playerMaxHp) * condMax, 0, condMax);
  const damageReceived = Math.max(0, Math.round(startHp - playerHp));
  const rewards = win ? (STAGE_REWARDS[stage] ?? { xp: 0, gold: 0, chest: false }) : { xp: 0, gold: 0, chest: false };
  // Rough pre-fight estimate for display only (ignores boosts; never decides).
  const winChance = calculateMatchAdvantage(baseTeamPower, eStats.physique + eStats.technique + eStats.vitesse + eStats.defense);

  const result: BossResult = {
    win,
    damageReceived,
    conditionAfter,
    xpEarned: Math.round(rewards.xp * mods.rewardMult),   // Greed scales XP…
    goldEarned: Math.round(rewards.gold * mods.rewardMult), // …and gold.
    chestEarned: rewards.chest,
    winChance,
  };

  return { frames, result, playerMaxHp, bossMaxHp };
}

/** Thin wrapper — runs a fight and returns only its result (no external callers use frames). */
export function resolveBoss(
  stage: number,
  playerStats: EquipmentStats,
  activeBonuses: PveBonus[],
  currentCondition: number,
  tier: number = DEFAULT_TIER
): BossResult {
  return simulateCombat(stage, playerStats, activeBonuses, currentCondition, tier).result;
}

export function isBonusStage(stage: number): boolean {
  return BONUS_STAGES.includes(stage);
}

export function applyBonusChoice(
  activeBonuses: PveBonus[],
  choice: PveBonus,
  replaceIdx?: number
): PveBonus[] {
  if (activeBonuses.length < MAX_ACTIVE_BONUSES) {
    return [...activeBonuses, choice];
  }
  if (replaceIdx !== undefined && replaceIdx >= 0 && replaceIdx < activeBonuses.length) {
    const next = [...activeBonuses];
    next[replaceIdx] = choice;
    return next;
  }
  return activeBonuses;
}

export function getStageRewards(stage: number) {
  return STAGE_REWARDS[stage] ?? { xp: 0, gold: 0, chest: false };
}
